import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Snackbar, IconButton, DialogContentText, Link, Checkbox, FormControlLabel, MenuItem, Select, InputLabel, FormControl } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useListPipelineSources, useCreatePipelineSource, useUpdatePipelineSource, useDeletePipelineSource, useScrapeSingleSource } from '../services/appService';
import io from 'socket.io-client';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';

function AiPipelinePage() {
  const { appId } = useParams();
  const listSources = useListPipelineSources();
  const createSource = useCreatePipelineSource();
  const deleteSource = useDeletePipelineSource();
  const updateSource = useUpdatePipelineSource();
  const scrapeSource = useScrapeSingleSource();

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ label: '', kind: 'url', url: '', schedule_type: 'one_time', polling_interval: '24 hours', include_subpages: false, subpage_filter_prompt: '', max_subpage_depth: 1 });
  const [editingSource, setEditingSource] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState(null);
  // Raw document viewer removed from this page per redesign

  const fetchSources = useCallback(async () => {
    if (!appId) return;
    try {
      setLoading(true);
      const data = await listSources(appId);
      setSources(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [appId, listSources]);

  useEffect(() => { fetchSources(); }, [fetchSources]);

  useEffect(() => {
    console.log('[ContentSources] Setting up socket connection...');
    const socket = io(process.env.REACT_APP_API_URL || '', {
       path: '/socket.io',
       transports: ['websocket'] // Prefer WebSocket
     });

    socket.on('connect', () => {
      console.log(`[ContentSources] Socket connected: ${socket.id}, joining room ${appId}`);
      socket.emit('joinRoom', String(appId)); // Ensure appId is string
    });

    // No orchestrator lifecycle; keep socket for potential future live scrape feedback

    socket.on('disconnect', (reason) => {
      console.log(`[ContentSources] Socket disconnected: ${reason}`);
    });

    socket.on('connect_error', (err) => {
      console.error('[ContentSources] Socket connection error:', err);
    });

    return () => {
      console.log('[ContentSources] Disconnecting socket...');
      socket.disconnect();
    };
  }, [appId, fetchSources]);

  const handleAdd = async () => {
    let newSourceId = null;
    try {
      const newSource = await createSource(appId, form);
      newSourceId = newSource?.id;
      setSnackbarMessage(`Source "${form.label}" added successfully!`);
      setSnackbarOpen(true);
      setAddOpen(false);
      setForm({ label: '', kind: 'url', url: '', schedule_type: 'one_time', polling_interval: '24 hours', include_subpages: false, subpage_filter_prompt: '', max_subpage_depth: 1 });
      fetchSources();
    } catch (e) {
      console.error("Error adding source:", e);
      setSnackbarMessage(`Error adding source: ${e.message}`);
      setSnackbarOpen(true);
    }

    if (newSourceId) {
      try {
         console.log(`[ResearchPage] Triggering scrape for new source ID: ${newSourceId}`);
         await scrapeSource(appId, newSourceId);
         setSnackbarMessage(`Source "${form.label}" added. Scrape initiated.`);
         setSnackbarOpen(true);
      } catch (scrapeError) {
         console.error("Error initiating scrape for new source:", scrapeError);
         setSnackbarMessage(`Source added, but failed to initiate scrape: ${scrapeError.message}`);
         setSnackbarOpen(true);
      }
    }
  };

  const openEdit = (source) => {
    setEditingSource(source);
    setForm({
      label: source.label || '',
      kind: source.kind || 'url',
      url: source.url || '',
      schedule_type: source.schedule_type || 'one_time',
      polling_interval: source.polling_interval || '24 hours',
      include_subpages: !!source.include_subpages,
      subpage_filter_prompt: source.subpage_filter_prompt || '',
      max_subpage_depth: source.max_subpage_depth || 1,
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingSource) return;
    try {
      await updateSource(appId, editingSource.id, form);
      setSnackbarMessage('Source updated');
      setSnackbarOpen(true);
      setEditOpen(false);
      setEditingSource(null);
      fetchSources();
    } catch (e) {
      setSnackbarMessage(`Error updating source: ${e.message}`);
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const openDeleteConfirm = (source) => {
    setSourceToDelete(source);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    setSourceToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleDeleteSource = async () => {
    if (!sourceToDelete) return;
    try {
      await deleteSource(appId, sourceToDelete.id);
      setSnackbarMessage(`Source "${sourceToDelete.label}" deleted successfully!`);
      setSnackbarOpen(true);
      fetchSources(); // Refresh the list
    } catch (error) {
      console.error("Error deleting source:", error);
      setSnackbarMessage(`Error deleting source: ${error.message}`);
      setSnackbarOpen(true);
    } finally {
      closeDeleteConfirm();
    }
  };

  const handleManualScrape = async (sourceId) => {
    console.log(`[ResearchPage] Manual scrape requested for source ID: ${sourceId}`);
    try {
      await scrapeSource(appId, sourceId);
      setSnackbarMessage(`Scrape initiated for source ${sourceId}.`);
      setSnackbarOpen(true);
    } catch (error) {
      console.error(`Error initiating scrape for source ${sourceId}:`, error);
      setSnackbarMessage(`Error initiating scrape: ${error.message}`);
      setSnackbarOpen(true);
    }
  };

  // Note: Content viewer logic removed from this page per redesign.

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'label', headerName: 'Label', flex: 1 },
    { field: 'kind', headerName: 'Kind', width: 120 },
    { field: 'url', headerName: 'URL', flex: 2, renderCell: (params) => (
        <Link href={params.value} target="_blank" rel="noopener noreferrer">{params.value}</Link>
    )},
    { field: 'schedule_type', headerName: 'Schedule', width: 120 },
    { field: 'polling_interval', headerName: 'Interval', width: 140, valueGetter: (params) => {
      const v = params?.row?.polling_interval;
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number') return `${v} ms`;
      if (typeof v === 'object') {
        const d = v;
        if (typeof d.humanize === 'function') return d.humanize();
        if (typeof d.humanize === 'string') return d.humanize;
        const parts = [];
        if (d.days) parts.push(`${d.days}d`);
        if (d.hours) parts.push(`${d.hours}h`);
        if (d.minutes) parts.push(`${d.minutes}m`);
        if (d.seconds) parts.push(`${d.seconds}s`);
        if (parts.length > 0) return parts.join(' ');
        try { return JSON.stringify(d); } catch { return String(d); }
      }
      return String(v);
    } },
    { field: 'include_subpages', headerName: 'Subpages', width: 110, type: 'boolean' },
    {
      field: 'last_checked_at',
      headerName: 'Last Crawled',
      width: 170,
      renderCell: (params) => {
        const v = params.value;
        if (!v) return '-';
        try {
          const d = new Date(v);
          if (isNaN(d.getTime())) return v;
          return d.toLocaleString('de-DE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        } catch {
          return v;
        }
      }
    },
    { field: 'is_active', headerName: 'Active', width: 90, type: 'boolean' },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160, 
      renderCell: (params) => {
        const createdAtValue = params.value;
        if (!createdAtValue) return '-';
        try {
          const date = new Date(createdAtValue);
          if (isNaN(date.getTime())) {
            console.warn(`[DataGrid Cell] Invalid date format for created_at: ${createdAtValue}`);
            return createdAtValue; // Show raw string if invalid
          }
          return date.toLocaleString('de-DE', { 
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
          });
        } catch (e) {
          console.error(`[DataGrid Cell] Error formatting date: ${createdAtValue}`, e);
          return createdAtValue; // Show raw string on error
        }
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            aria-label="edit"
            size="small"
            title="Edit Source"
            onClick={() => openEdit(params.row)}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            aria-label="scrape"
            size="small"
            title="Scrape Source Now"
            onClick={() => handleManualScrape(params.row.id)}
          >
            <RefreshIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            aria-label="delete"
            size="small"
            title="Delete Source"
            onClick={() => openDeleteConfirm(params.row)}
          >
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </Box>
      )
    }
  ];

  // Raw documents and discovery UI removed

  return (
    <Box sx={{ px: 1, py: 2, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - 32px)' }}>
      <Typography variant="h5" gutterBottom>Content Sources</Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
          <Button variant="contained" onClick={() => setAddOpen(true)}>Add Source</Button>
        </Box>
        <Box sx={{ flexGrow: 1, width: '100%' }}>
           {loading ? <CircularProgress /> : error ? <Alert severity="error">{error}</Alert> : (
             <DataGrid 
               rows={sources} 
               columns={columns} 
               pageSize={100}
               rowsPerPageOptions={[25, 50, 100]}
               sx={{ height: '100%' }}
             />
           )}
         </Box>
       </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Content Source</DialogTitle>
        <DialogContent>
          <TextField margin="normal" label="Label" fullWidth value={form.label} onChange={e=>setForm({...form,label:e.target.value})}/>
          <TextField margin="normal" label="Kind (url|rss|opendata|image_url|pdf_url)" fullWidth value={form.kind} onChange={e=>setForm({...form,kind:e.target.value})}/>
          <TextField margin="normal" label="URL" fullWidth value={form.url} onChange={e=>setForm({...form,url:e.target.value})}/>
          <FormControl margin="normal" fullWidth>
            <InputLabel id="schedule-type-label">Schedule</InputLabel>
            <Select labelId="schedule-type-label" label="Schedule" value={form.schedule_type} onChange={(e)=>setForm({...form, schedule_type: e.target.value})}>
              <MenuItem value="one_time">One-time</MenuItem>
              <MenuItem value="recurring">Recurring</MenuItem>
            </Select>
          </FormControl>
          {form.schedule_type === 'recurring' && (
            <TextField margin="normal" label="Interval (e.g. 24 hours)" fullWidth value={form.polling_interval} onChange={e=>setForm({...form, polling_interval: e.target.value})}/>
          )}
          <FormControlLabel control={<Checkbox checked={form.include_subpages} onChange={(e)=>setForm({...form, include_subpages: e.target.checked})}/>} label="Also crawl subpages (max 1 level)" />
          {form.include_subpages && (
            <TextField margin="normal" label="Subpage filter (natural language)" fullWidth multiline minRows={2} value={form.subpage_filter_prompt} onChange={e=>setForm({...form, subpage_filter_prompt: e.target.value})}/>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={()=>{setEditOpen(false); setEditingSource(null);}} fullWidth maxWidth="sm">
        <DialogTitle>Edit Content Source</DialogTitle>
        <DialogContent>
          <TextField margin="normal" label="Label" fullWidth value={form.label} onChange={e=>setForm({...form,label:e.target.value})}/>
          <TextField margin="normal" label="Kind (url|rss|opendata|image_url|pdf_url)" fullWidth value={form.kind} onChange={e=>setForm({...form,kind:e.target.value})}/>
          <TextField margin="normal" label="URL" fullWidth value={form.url} onChange={e=>setForm({...form,url:e.target.value})}/>
          <FormControl margin="normal" fullWidth>
            <InputLabel id="edit-schedule-type-label">Schedule</InputLabel>
            <Select labelId="edit-schedule-type-label" label="Schedule" value={form.schedule_type} onChange={(e)=>setForm({...form, schedule_type: e.target.value})}>
              <MenuItem value="one_time">One-time</MenuItem>
              <MenuItem value="recurring">Recurring</MenuItem>
            </Select>
          </FormControl>
          {form.schedule_type === 'recurring' && (
            <TextField margin="normal" label="Interval (e.g. 24 hours)" fullWidth value={form.polling_interval} onChange={e=>setForm({...form, polling_interval: e.target.value})}/>
          )}
          <FormControlLabel control={<Checkbox checked={form.include_subpages} onChange={(e)=>setForm({...form, include_subpages: e.target.checked})}/>} label="Also crawl subpages (max 1 level)" />
          {form.include_subpages && (
            <TextField margin="normal" label="Subpage filter (natural language)" fullWidth multiline minRows={2} value={form.subpage_filter_prompt} onChange={e=>setForm({...form, subpage_filter_prompt: e.target.value})}/>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>{setEditOpen(false); setEditingSource(null);}}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the source "{sourceToDelete?.label || ''}"?
            This will also delete all associated raw documents and their stored content.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteSource} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Raw document viewer & discovery removed in this redesign */}

      <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default AiPipelinePage; 